import { getBackendUrl } from '../env'
import { useAuthContext } from '../hooks/useAuthContext'
import { useSystemsContext } from '../hooks/useSystemsContext'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getDeviceReading, getDeviceReadingsSparkline, getDevicesInSystem } from '../helper/deviceHelper'
import { System } from '../context/SystemContext'
import DeleteAlert from './DeleteAlert'
import { Settings, Trash2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { MetricTile } from './systemMetrics/MetricTile'
import {
    classifyDeviceType,
    parseReadingToDisplayNumber,
} from './systemMetrics/metricHelpers'

const MAX_SPARKLINE_POINTS = 36

interface SystemDetailsProps {
    system: System
    setSystemPopup: React.Dispatch<React.SetStateAction<boolean>>
    setNewSystem: React.Dispatch<React.SetStateAction<boolean>>
    setSystem: React.Dispatch<React.SetStateAction<System>>
}

type DeviceReadingRow = {
    key: string
    name: string
    type: string
}

const SystemDetails = ({ system, setSystemPopup, setNewSystem, setSystem }: SystemDetailsProps) => {
    const [openDeleteAlert, setOpenDeleteAlert] = useState(false)
    const { user } = useAuthContext()
    const { dispatch } = useSystemsContext()
    const [devicesInSystem, setDevicesInSystem] = useState<any[]>([])
    const [deviceReadingsList, setDeviceReadingsList] = useState<DeviceReadingRow[]>([])
    const [readingHistory, setReadingHistory] = useState<Record<string, number[]>>({})
    const [latestByDevice, setLatestByDevice] = useState<Record<string, number | null>>({})
    const [lastFetchAt, setLastFetchAt] = useState<Date | null>(null)
    const [loadingReadings, setLoadingReadings] = useState(false)
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const handleSettings = async () => {
        setSystem(system)
        setNewSystem(false)
        setSystemPopup(true)
    }

    const getData = useCallback(async () => {
        if (!user) return
        const devices = await getDevicesInSystem(user, system._id)
        if (mountedRef.current) {
            setDevicesInSystem(Array.isArray(devices) ? devices : [])
        }
    }, [user, system._id])

    const getDeviceReadings = useCallback(async () => {
        if (!user) return
        setLoadingReadings(true)

        const results = await Promise.all(
            devicesInSystem.map(async (device: any) => {
                if (device.device_type?.includes('Pump') || device.device_type?.includes('Outlet')) {
                    return null
                }
                const result = await getDeviceReading(user, device.device_id)
                const category = classifyDeviceType(device.device_type ?? '')
                const displayValue = parseReadingToDisplayNumber(String(result.reading), category)
                return {
                    key: device.device_id,
                    name: device.name,
                    type: device.device_type,
                    displayValue,
                }
            })
        )

        const filtered = results.filter(
            (r): r is { key: string; name: string; type: string; displayValue: number | null } => r !== null
        )

        if (!mountedRef.current) return

        setLatestByDevice(() => {
            const next: Record<string, number | null> = {}
            for (const r of filtered) {
                next[r.key] = r.displayValue
            }
            return next
        })

        setReadingHistory((prev) => {
            const next = { ...prev }
            for (const r of filtered) {
                if (r.displayValue === null) continue
                const arr = [...(next[r.key] ?? []), r.displayValue]
                next[r.key] =
                    arr.length > MAX_SPARKLINE_POINTS
                        ? arr.slice(arr.length - MAX_SPARKLINE_POINTS)
                        : arr
            }
            return next
        })

        setDeviceReadingsList(filtered.map(({ key, name, type }) => ({ key, name, type })))
        setLastFetchAt(new Date())
        setLoadingReadings(false)
    }, [user, devicesInSystem])

    useEffect(() => {
        setDeviceReadingsList([])
        setReadingHistory({})
        setLatestByDevice({})
        setLastFetchAt(null)
        void getData()
    }, [getData])

    useEffect(() => {
        if (devicesInSystem.length > 0) {
            void getDeviceReadings()
        }
    }, [devicesInSystem, getDeviceReadings])

    useEffect(() => {
        if (!user || devicesInSystem.length === 0) return
        let cancelled = false
        void (async () => {
            const sensors = devicesInSystem.filter(
                (d: { device_type?: string }) =>
                    !(d.device_type?.includes('Pump') || d.device_type?.includes('Outlet'))
            )
            const entries = await Promise.all(
                sensors.map(async (device: { device_id: string; device_type?: string }) => {
                    const res = await getDeviceReadingsSparkline(user, device.device_id, {
                        limit: MAX_SPARKLINE_POINTS,
                        hours: 72,
                    })
                    if (
                        res &&
                        typeof res === 'object' &&
                        'values' in res &&
                        Array.isArray((res as { values: number[] }).values)
                    ) {
                        const category = classifyDeviceType(device.device_type ?? '')
                        const values = (res as { values: number[] }).values
                            .map((v) => parseReadingToDisplayNumber(String(v), category))
                            .filter((n): n is number => n !== null)
                        return [device.device_id, values] as const
                    }
                    return [device.device_id, [] as number[]] as const
                })
            )
            if (cancelled) return
            setReadingHistory((prev) => {
                const next = { ...prev }
                for (const [key, values] of entries) {
                    if (values.length > 0) {
                        next[key] = values
                    }
                }
                return next
            })
        })()
        return () => {
            cancelled = true
        }
    }, [user, devicesInSystem])

    useEffect(() => {
        const intervalId = setInterval(() => {
            void getDeviceReadings()
        }, 60000)
        return () => clearInterval(intervalId)
    }, [getDeviceReadings])

    const handleDelete = async () => {
        if (!user) {
            return
        }
        const response = await fetch(`${getBackendUrl()}/api/systems/` + system._id, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${user.token}`,
            },
        })
        const json = await response.json()
        if (response.ok) {
            dispatch({ type: 'DELETE_SYSTEM', payload: json })
            devicesInSystem.forEach(async (device: any) => {
                const response2 = await fetch(`${getBackendUrl()}/api/devices/` + device._id, {
                    method: 'PATCH',
                    body: JSON.stringify({ system_id: '' }),
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${user.token}`,
                    },
                })
                if (response2.ok) {
                    /* device unlinked */
                }
            })
        }
    }

    const programType =
        system && typeof system === 'object' && 'programType' in system
            ? String((system as { programType?: string }).programType ?? 'Manual')
            : 'Manual'
    const programName =
        system && typeof system === 'object' && 'program' in system && (system as { program?: { name?: string } }).program
            ? (system as { program?: { name?: string } }).program?.name
            : undefined
    const reservoir =
        system && typeof system === 'object' && 'reservoir_size' in system
            ? (system as { reservoir_size?: number }).reservoir_size
            : undefined

    const uniqueKey = (row: DeviceReadingRow, index: number) => `${row.key}-${index}`

    return (
        <>
            <div
                className={cn(
                    'flex min-w-0 w-full max-w-lg flex-col gap-3 rounded-xl border border-zinc-700/90 bg-zinc-950/80 p-4 shadow-lg ring-1 ring-zinc-800/60'
                )}
            >
                <div className="flex flex-col gap-2 border-b border-zinc-800/90 pb-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <h4 className="truncate text-lg font-semibold tracking-tight text-zinc-50">{system.name}</h4>
                        <div className="flex shrink-0 gap-1">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                                onClick={() => void handleSettings()}
                                aria-label="System settings"
                            >
                                <Settings className="size-4" />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-zinc-400 hover:bg-red-900/40 hover:text-red-200"
                                onClick={() => setOpenDeleteAlert(true)}
                                aria-label="Delete system"
                            >
                                <Trash2 className="size-4" />
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 text-xs">
                        <span
                            className={cn(
                                'rounded-full border px-2 py-0.5 font-medium',
                                programType === 'Automatic'
                                    ? 'border-emerald-500/40 bg-emerald-950/50 text-emerald-200'
                                    : 'border-zinc-600 bg-zinc-900 text-zinc-300'
                            )}
                        >
                            {programType}
                        </span>
                        {programType === 'Automatic' && programName ? (
                            <span className="rounded-full border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 text-zinc-400">
                                {programName}
                            </span>
                        ) : null}
                        <span className="rounded-full border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 text-zinc-400">
                            {devicesInSystem.length} device{devicesInSystem.length !== 1 ? 's' : ''}
                        </span>
                        {reservoir !== undefined && reservoir !== null ? (
                            <span className="rounded-full border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 text-zinc-400">
                                Reservoir {reservoir} L
                            </span>
                        ) : null}
                    </div>

                    <p className="text-[11px] text-zinc-500">
                        {loadingReadings && !lastFetchAt
                            ? 'Loading readings…'
                            : lastFetchAt
                              ? `Updated ${formatDistanceToNow(lastFetchAt, { addSuffix: true })}`
                              : 'No readings yet'}
                    </p>
                </div>

                {deviceReadingsList.length === 0 ? (
                    <p className="py-6 text-center text-sm text-zinc-500">
                        No sensor readings yet. Add sensors to this system to see live metrics.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {deviceReadingsList.map((row, index) => {
                            const category = classifyDeviceType(row.type)
                            const hist = readingHistory[row.key] ?? []
                            return (
                                <MetricTile
                                    key={uniqueKey(row, index)}
                                    system={system}
                                    name={row.name}
                                    deviceType={row.type}
                                    category={category}
                                    displayValue={latestByDevice[row.key] ?? null}
                                    history={hist}
                                    programType={programType}
                                />
                            )
                        })}
                    </div>
                )}
            </div>

            <DeleteAlert
                openDeleteAlert={openDeleteAlert}
                setOpenDeleteAlert={setOpenDeleteAlert}
                handleDelete={handleDelete}
                titleMessage={'Delete system'}
                message={'Are you sure you want to delete ' + system.name + '?'}
            />
        </>
    )
}

export default SystemDetails
